type FormDataValue = string | number | boolean | File;
type NestedObject = {
  [key: string]:
    | FormDataValue
    | NestedObject
    | Array<FormDataValue | NestedObject>
    | null
    | undefined;
};

/**
 * Builds FormData from a nested object structure, using Rails-style parameter naming
 * Example: { user: { profile: { name: "John" } } } becomes "user[profile][name]" = "John"
 *
 * @param data - The data object to convert to FormData
 * @param options - Configuration options
 * @returns FormData object ready to be sent
 */
export function buildFormData(
  data: NestedObject,
  options: {
    fileField?: { key: string; value: File | "" };
  } = {},
): FormData {
  const formData = new FormData();

  function appendToFormData(
    obj: FormDataValue | NestedObject | Array<FormDataValue | NestedObject>,
    parentKey: string,
    formData: FormData,
  ): void {
    if (obj === null || obj === undefined) {
      return;
    }

    // Handle arrays with Rails-style [] notation
    if (Array.isArray(obj)) {
      obj.forEach((item) => {
        if (item !== null && item !== undefined) {
          appendToFormData(item, `${parentKey}[]`, formData);
        }
      });
      return;
    }

    if (obj instanceof File) {
      formData.append(parentKey, obj);
      return;
    }

    if (typeof obj === "object") {
      Object.keys(obj).forEach((key) => {
        const value = (obj as NestedObject)[key];
        const formKey = parentKey ? `${parentKey}[${key}]` : key;

        if (value !== null && value !== undefined) {
          appendToFormData(value, formKey, formData);
        }
      });
    } else {
      formData.append(parentKey, String(obj));
    }
  }

  appendToFormData(data, "", formData);

  if (options.fileField) {
    formData.append(options.fileField.key, options.fileField.value);
  }

  return formData;
}
